// iter587 — „Днешна интервенция" с реални данни + жива верига от препоръки.
// Чете GET /api/dashboard/today-intervention (daily_tracking + mindbody логове).
// Изпълнено действие → ✓ → автоматично показва СЛЕДВАЩАТА препоръка.
// Бързо записване (движение/вода/сън) директно от картата → POST /api/tracking/daily.
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

export default function TodayInterventionCard({ API, isDark, bg, refreshSignal, onLogged, mealDilData, dilBudget }) {
  const [data, setData] = useState(null);
  const [inputVal, setInputVal] = useState("");
  const [saving, setSaving] = useState(false);
  // iter589 — вечерен мини-отчет: след 20:00 картата затваря деня емоционално.
  const isEvening = new Date().getHours() >= 20;
  const [recap, setRecap] = useState(null);
  const [showChain, setShowChain] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/dashboard/today-intervention?hour=${new Date().getHours()}`);
      setData(d);
    } catch {
      // silent
    }
  }, [API]);

  const fetchRecap = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/dashboard/evening-recap?hour=${new Date().getHours()}`);
      setRecap(d);
    } catch {
      // silent
    }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData, refreshSignal]);
  useEffect(() => { if (isEvening) fetchRecap(); }, [isEvening, fetchRecap, refreshSignal]);

  const cur = data?.current;

  const quickLog = async () => {
    if (!cur?.quick_log) return;
    const { type, key } = cur.quick_log;
    let value;
    if (type === "add_glass") {
      value = (cur.progress?.current || 0) + 1;
    } else {
      const v = parseFloat(inputVal);
      if (!v || v <= 0) {
        toast.error(bg ? "Въведи число." : "Enter a number.");
        return;
      }
      value = type === "add_minutes" ? (cur.progress?.current || 0) + v : v;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/tracking/daily`, { key, value });
      setInputVal("");
      toast.success(bg ? "Записано ✓" : "Logged ✓");
      await fetchData();
      if (isEvening) await fetchRecap();
      if (onLogged) onLogged();
    } catch {
      toast.error(bg ? "Грешка при запис." : "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // iter589 — Вечерен изглед „Как мина денят Ви" (20:00+)
  if (isEvening && !showChain && recap) {
    const used = Math.round((mealDilData?.daily_effective_dil ?? 0) * 10) / 10;
    const total = Math.round((dilBudget || 97) * 10) / 10;
    const dilPct = Math.min(150, Math.round((used / Math.max(total, 1)) * 100));
    const dilColor = dilPct <= 80 ? "#10B981" : dilPct <= 100 ? "#F59E0B" : "#F43F5E";
    return (
      <div
        className={`rounded-2xl border p-5 transition-all ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5 hover:border-indigo-500/30" : "bg-white border-gray-200 hover:border-indigo-200"}`}
        data-testid="cc-evening-recap"
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xl" aria-hidden="true">🌙</span>
            <h3 className={`text-sm font-bold uppercase tracking-wider truncate ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {bg ? "Как мина денят Ви" : "How your day went"}
            </h3>
          </div>
          <span className={`shrink-0 text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full ${isDark ? "bg-indigo-500/15 text-indigo-300" : "bg-indigo-50 text-indigo-700"}`} data-testid="cc-recap-position">
            {recap.completed_count}/{recap.total}
          </span>
        </div>

        {/* Стъпките от деня */}
        <div className="flex flex-wrap gap-1.5" data-testid="cc-recap-steps">
          {recap.steps.map((s) => (
            <span
              key={s.id}
              title={s.title}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                s.done
                  ? (isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700")
                  : (isDark ? "bg-white/5 text-gray-500" : "bg-gray-100 text-gray-400")
              }`}
            >
              {s.icon} {s.done ? "✓" : "○"}
            </span>
          ))}
        </div>

        {/* DIL баланс на деня */}
        <div className="mt-3" data-testid="cc-recap-dil">
          <div className={`flex items-center justify-between text-[11px] font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            <span>{bg ? "Инсулинов бюджет днес" : "Insulin budget today"}</span>
            <span className="tabular-nums" style={{ color: dilColor }}>{used} / {total} g ({dilPct}%)</span>
          </div>
          <div className={`mt-1 w-full h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
            <div className="h-full transition-all duration-500" style={{ width: `${Math.min(100, dilPct)}%`, backgroundColor: dilColor }} />
          </div>
        </div>

        {/* 1 похвала */}
        <div className={`mt-3 text-xs leading-relaxed rounded-xl px-3 py-2.5 ${isDark ? "bg-emerald-500/10 text-emerald-100" : "bg-emerald-50 text-emerald-900"}`} data-testid="cc-recap-praise">
          🌟 {bg ? recap.praise_bg : recap.praise_en}
        </div>

        {/* 1 съвет за утре */}
        <div className={`mt-2 text-xs leading-relaxed rounded-xl px-3 py-2.5 ${isDark ? "bg-amber-500/10 text-amber-100" : "bg-amber-50 text-amber-900"}`} data-testid="cc-recap-advice">
          🌱 {bg ? "Утре:" : "Tomorrow:"} {bg ? recap.advice_bg : recap.advice_en}
        </div>

        {recap.completed_count < recap.total && (
          <button
            type="button"
            onClick={() => setShowChain(true)}
            data-testid="cc-recap-show-chain"
            className={`mt-3 text-xs font-semibold ${isDark ? "text-indigo-300 hover:text-indigo-200" : "text-indigo-700 hover:text-indigo-900"}`}
          >
            ✎ {bg ? "Запишете пропуснатото" : "Log something missed"}
          </button>
        )}
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`rounded-2xl border p-5 ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5" : "bg-white border-gray-200"}`} data-testid="cc-today-intervention">
        <div className={`h-4 w-2/3 rounded animate-pulse ${isDark ? "bg-white/10" : "bg-gray-100"}`} />
      </div>
    );
  }

  const pct = cur?.progress?.target
    ? Math.min(100, Math.round((cur.progress.current / cur.progress.target) * 100))
    : 0;

  return (
    <div
      className={`rounded-2xl border p-5 transition-all ${isDark ? "bg-[hsl(222,47%,9%)] border-white/5 hover:border-violet-500/30" : "bg-white border-gray-200 hover:border-violet-200"}`}
      data-testid="cc-today-intervention"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xl" aria-hidden="true">{cur?.icon || "🎉"}</span>
          <h3 className={`text-sm font-bold uppercase tracking-wider truncate ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {bg ? "Днешна интервенция" : "Today's intervention"}
          </h3>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1.5">
          {data.daypart_bg && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isDark ? "bg-white/5 text-gray-400" : "bg-gray-100 text-gray-500"}`} data-testid="cc-intervention-daypart">
              {bg ? data.daypart_bg : data.daypart_en}
            </span>
          )}
          <span className={`text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full ${isDark ? "bg-violet-500/15 text-violet-300" : "bg-violet-50 text-violet-700"}`} data-testid="cc-intervention-position">
            {data.position}
          </span>
        </span>
      </div>

      {data.all_done ? (
        <div data-testid="cc-intervention-all-done">
          <div className={`text-xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            🎉 {bg ? "Всичко за днес е изпълнено" : "All done for today"}
          </div>
          <p className={`mt-2 text-xs leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {bg
              ? "Движение, вода, спокойствие и сън — тялото ти благодари. Утре продължаваме."
              : "Movement, water, calm and sleep — your body thanks you. See you tomorrow."}
          </p>
        </div>
      ) : (
        <>
          <div className={`text-xl font-bold leading-snug ${isDark ? "text-white" : "text-[#1A1A2E]"}`} data-testid="cc-intervention-title">
            {cur.title}
          </div>
          <p className={`mt-1 text-xs leading-relaxed ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {cur.why}
          </p>

          {/* Прогрес от реалните записи */}
          {cur.progress?.target > 1 && (
            <div className="mt-3" data-testid="cc-intervention-progress">
              <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? "bg-white/5" : "bg-gray-100"}`}>
                <div className="h-full bg-violet-500 transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className={`mt-1 text-[11px] tabular-nums ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {cur.progress.current} / {cur.progress.target} {cur.progress.unit}
              </div>
            </div>
          )}

          <ul className={`mt-2.5 text-xs space-y-0.5 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            {(cur.effects || []).map((e) => <li key={e}>{e}</li>)}
          </ul>

          {/* Бързо записване — таблото веднага научава и дава следващата стъпка */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {cur.quick_log?.type === "add_glass" && (
              <button
                type="button" onClick={quickLog} disabled={saving}
                data-testid="cc-intervention-quick-log"
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${isDark ? "bg-sky-500/20 text-sky-200 hover:bg-sky-500/30" : "bg-sky-100 text-sky-900 hover:bg-sky-200"}`}
              >
                {saving ? "…" : `+1 ${bg ? "чаша" : "glass"}`}
              </button>
            )}
            {(cur.quick_log?.type === "add_minutes" || cur.quick_log?.type === "set_hours") && (
              <span className="inline-flex items-center gap-1.5">
                <input
                  type="number" min="0" step={cur.quick_log.type === "set_hours" ? "0.5" : "5"}
                  value={inputVal} onChange={(e) => setInputVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") quickLog(); }}
                  placeholder={cur.progress?.unit}
                  data-testid="cc-intervention-quick-input"
                  className={`w-20 px-2.5 py-1.5 rounded-xl text-xs font-bold tabular-nums border ${isDark ? "bg-white/5 border-white/10 text-white" : "bg-gray-50 border-gray-200 text-[#1A1A2E]"}`}
                />
                <button
                  type="button" onClick={quickLog} disabled={saving}
                  data-testid="cc-intervention-quick-log"
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${isDark ? "bg-violet-500/20 text-violet-200 hover:bg-violet-500/30" : "bg-violet-100 text-violet-900 hover:bg-violet-200"}`}
                >
                  {saving ? "…" : (bg ? "Запиши" : "Log")}
                </button>
              </span>
            )}
            {cur.cta_path && (
              <Link
                to={cur.cta_path}
                className={`text-xs font-semibold inline-flex items-center gap-1 ${isDark ? "text-violet-300 hover:text-violet-200" : "text-violet-700 hover:text-violet-900"}`}
                data-testid="cc-intervention-cta"
              >
                ➜ {cur.cta_label}
              </Link>
            )}
          </div>
        </>
      )}

      {/* Изпълнено днес — таблото го знае и го отбелязва */}
      {data.completed?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="cc-intervention-done-list">
          {data.completed.map((c) => (
            <span
              key={c.id}
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${isDark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-50 text-emerald-700"}`}
            >
              ✓ {c.title}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
